import axios, { AxiosError } from "axios";
import { useQuery, UseQueryResult } from "react-query";
import { ShiftSitesOverviewResponse } from "../model/ShiftSite";
import { ShiftSitesOverviewRequest } from "../request/ShiftSitesOverviewRequest";
import { HttpClientError } from "../../../../providers/HttpClient";

const ShiftSitesOverviewApiService = async (
  request: Omit<ShiftSitesOverviewRequest, "enabled" | "refetchInterval">
): Promise<ShiftSitesOverviewResponse> => {
  try {
    const { data } = await axios.get("/shift-sites/overview/", {
      params: {
        ...(request.employer !== undefined && { employer: request.employer }),
        ...(request.date_from && { date_from: request.date_from }),
        ...(request.date_to && { date_to: request.date_to }),
        ...(request.client && { client: request.client }),
      },
    });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);

    customError.cause = axiosError;
    throw customError;
  }
};

const useShiftSitesOverviewApiService = (
  request: ShiftSitesOverviewRequest
): UseQueryResult<ShiftSitesOverviewResponse, Error | AxiosError> => {
  const { enabled = true, refetchInterval = false, ...queryParams } = request;
  const normalizedParams = {
    employer: queryParams.employer ?? null,
    date_from: queryParams.date_from ?? null,
    date_to: queryParams.date_to ?? null,
    client: queryParams.client ?? null,
  };
  return useQuery<ShiftSitesOverviewResponse, Error | AxiosError>(
    ["ShiftSitesOverviewApiService", ...Object.values(normalizedParams)],
    () => ShiftSitesOverviewApiService(queryParams),
    { keepPreviousData: true, enabled, refetchInterval }
  );
};

export { ShiftSitesOverviewApiService, useShiftSitesOverviewApiService };
